import { withRouter } from "react-router-dom";
import { useEffect, useState } from "react";
import { BbotModal, BbotTable, OPContainer, Switch, notification } from "top-component-library";
import BbotButton from "top-component-library/BbotButton";
import { BBOT_DJANGO_SERVER } from "Config";
import { generalErrorAlert } from "util/Utils";
import { getDeviceProfiles, editDeviceProfile, deleteDeviceProfiles } from "./utils";
import { Fields } from "./consts";
import DeviceProfileModal from "./modals/DeviceProfileModal";
import { LegacyLinkAlert } from "./styles";

const PAGINATION_CONFIG = {
  defaultPageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ["5", "10", "25", "50"],
};

const DeviceProfiles = (props) => {
  const [deviceProfiles, setDeviceProfiles] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const refresh = () => getDeviceProfiles(setDeviceProfiles);

  useEffect(() => {
    refresh();
  }, []);

  const openAdd = () => {
    setEditingConfig(null);
    setModalVisible(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingConfig(null);
  };

  const confirmDelete = async () => {
    try {
      await deleteDeviceProfiles(pendingDeleteIds);
      notification.success({
        message: pendingDeleteIds.length > 1 ? "Device profiles deleted." : "Device profile deleted.",
      });
      setPendingDeleteIds([]);
      refresh();
    } catch (error) {
      generalErrorAlert(error, "Trouble deleting device profiles. Please try again.", true);
    }
  };

  const legacyLink =
    BBOT_DJANGO_SERVER +
    (props.userInfo?.role === "admin"
      ? "/superuser/console/#!/edit/handheldConfigs"
      : "/owner/console/#!/edit/handheldConfigs");

  const customerId = props?.selectedCustomer?.customer_id;

  // In bulk-selection mode BbotTable toggles row selection on row click, so stop clicks on the
  // interactive cells from also (de)selecting the row.
  const stopRowClick = (event) => event.stopPropagation();

  const toggleColumn = (title, field) => ({
    title,
    key: field,
    render: (_value, row) => (
      <span onClick={stopRowClick}>
        <Switch
          checked={!!row[field]}
          onChange={(checked) =>
            editDeviceProfile({ id: row.id, field, value: checked, customer_id: customerId, setDeviceProfiles })
          }
        />
      </span>
    ),
  });

  const columns = [
    { title: "Name", dataIndex: Fields.ConfigName, key: Fields.ConfigName },
    { title: "Description", dataIndex: Fields.Description, key: Fields.Description },
    toggleColumn("Locations Overview", Fields.ShowLocationsOverview),
    toggleColumn("Create Order", Fields.ShowCreateOrder),
    toggleColumn("Has Chipper", Fields.UseStripeChipReader),
    toggleColumn("Stations Overview", Fields.ShowStationsOverview),
    toggleColumn("KDS View", Fields.ShowKDSView),
    toggleColumn("Menu Management", Fields.ShowMenuEditor),
    toggleColumn("Order Control", Fields.ShowOrderControl),
    toggleColumn("Tip Report", Fields.ShowTipReport),
    {
      title: "Edit",
      key: "edit",
      render: (_value, row) => (
        <span onClick={stopRowClick}>
          <BbotButton type="link" onClick={() => openEdit(row)}>
            Edit
          </BbotButton>
        </span>
      ),
    },
  ];

  const tableData = deviceProfiles.map((profile) => ({ ...profile, key: profile.id }));

  return (
    <OPContainer
      title="Device Profiles"
      url="/profiles"
      selectedCustomer={props.selectedCustomer}
      userInfo={props.userInfo}
    >
      <LegacyLinkAlert
        message={
          <span>
            We&apos;ve updated the Device Profiles page. To access the old page, follow this{" "}
            <a href={legacyLink}>link</a>.
          </span>
        }
      />
      <BbotTable
        id="device-profiles"
        title="Available Devices"
        titleRight={
          <BbotButton type="primary" onClick={openAdd}>
            Add Device Profile
          </BbotButton>
        }
        manuallyFormattedColumns={columns}
        data={tableData}
        pagination={PAGINATION_CONFIG}
        interactive={true}
        allowBulkDeletion={true}
        onBulkDelete={(ids) => setPendingDeleteIds(ids)}
      />
      <BbotModal
        title="Are you sure you'd like to delete these device profiles?"
        visible={pendingDeleteIds.length > 0}
        onCancel={() => setPendingDeleteIds([])}
        okText="Delete"
        okType="danger"
        onOk={confirmDelete}
      >
        You are about to delete {pendingDeleteIds.length} device profile{pendingDeleteIds.length > 1 ? "s" : ""}.
      </BbotModal>
      <DeviceProfileModal
        visible={modalVisible}
        config={editingConfig}
        selectedCustomer={props.selectedCustomer}
        onClose={closeModal}
        onSaved={refresh}
      />
    </OPContainer>
  );
};

export default withRouter(DeviceProfiles);
